// Attachments of a purchase request for the Purchase panel's "View details"
// row: image thumbnails inline, and a click opens the file in an in-page
// preview (image / PDF / video / audio, anything else can be downloaded).
// Files come through the authenticated attachment endpoint as blobs, since
// the S3 bucket isn't publicly readable.
import React, { useCallback, useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Download, FileText, File as FileIcon, Image as ImageIcon, Loader2 } from 'lucide-react';
import { downloadPurchaseRequestAttachment } from '../../../api';
import './AddPurchaseRequestModal.css';
import './PurchaseRequestAttachments.css';

const MIME_BY_EXTENSION = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    svg: 'image/svg+xml',
    pdf: 'application/pdf',
    mp4: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    m4a: 'audio/mp4',
    txt: 'text/plain',
};

const extensionOf = (attachment) => {
    const name = attachment?.filename || attachment?.fileUrl || '';
    const match = /\.([a-z0-9]+)(?:[?#].*)?$/i.exec(name);
    return match ? match[1].toLowerCase() : '';
};

const mimeFromName = (attachment) => MIME_BY_EXTENSION[extensionOf(attachment)] || '';

const kindOf = (mime) => {
    if (mime.startsWith('image/')) return 'image';
    if (mime === 'application/pdf') return 'pdf';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    if (mime === 'text/plain') return 'text';
    return 'other';
};

const canCreateObjectUrl = () => typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';

// Fetches one attachment and returns an object URL plus its preview kind.
// A generic content type (older uploads) falls back to the file extension so
// PDFs / images still render inline instead of downloading.
const fetchAttachment = async (requestId, attachment) => {
    const response = await downloadPurchaseRequestAttachment(requestId, attachment._id);
    let blob = response?.data;
    if (!(blob instanceof Blob)) throw new Error('Attachment could not be loaded');
    const nameMime = mimeFromName(attachment);
    if ((!blob.type || blob.type === 'application/octet-stream') && nameMime) {
        blob = new Blob([blob], { type: nameMime });
    }
    return { url: URL.createObjectURL(blob), kind: kindOf(blob.type || nameMime) };
};

function AttachmentPreviewModal({ requestId, attachments, startIndex, thumbnails, onClose }) {
    const [index, setIndex] = useState(startIndex);
    const [state, setState] = useState({ loading: true, url: '', kind: '', error: '' });
    const attachment = attachments[index];
    const total = attachments.length;
    const cachedUrl = attachment ? thumbnails[attachment._id] : '';

    const goPrev = useCallback(() => setIndex((prev) => (prev - 1 + total) % total), [total]);
    const goNext = useCallback(() => setIndex((prev) => (prev + 1) % total), [total]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
            if (total > 1 && event.key === 'ArrowLeft') goPrev();
            if (total > 1 && event.key === 'ArrowRight') goNext();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose, goPrev, goNext, total]);

    useEffect(() => {
        if (!attachment) return undefined;
        // Image thumbnails are already loaded - reuse them.
        if (cachedUrl) {
            setState({ loading: false, url: cachedUrl, kind: 'image', error: '' });
            return undefined;
        }
        if (!canCreateObjectUrl()) {
            setState({ loading: false, url: '', kind: '', error: 'Preview is not supported in this browser' });
            return undefined;
        }

        let cancelled = false;
        let ownUrl = '';
        setState({ loading: true, url: '', kind: '', error: '' });
        fetchAttachment(requestId, attachment)
            .then(({ url, kind }) => {
                ownUrl = url;
                if (cancelled) {
                    URL.revokeObjectURL(url);
                    return;
                }
                setState({ loading: false, url, kind, error: '' });
            })
            .catch(() => {
                if (!cancelled) setState({ loading: false, url: '', kind: '', error: 'Could not load this attachment' });
            });

        return () => {
            cancelled = true;
            if (ownUrl) URL.revokeObjectURL(ownUrl);
        };
    }, [requestId, attachment, cachedUrl]);

    if (!attachment) return null;
    const filename = attachment.filename || 'Attachment';

    const renderBody = () => {
        if (state.loading) {
            return (
                <div className="pra-preview-message">
                    <Loader2 size={22} className="pra-spin" aria-hidden="true" />
                    Loading preview…
                </div>
            );
        }
        if (state.error) return <div className="pra-preview-message">{state.error}</div>;
        if (state.kind === 'image') return <img className="pra-preview-image" src={state.url} alt={filename} />;
        if (state.kind === 'pdf' || state.kind === 'text') {
            return <iframe className="pra-preview-frame" src={state.url} title={filename} />;
        }
        if (state.kind === 'video') {
            // eslint-disable-next-line jsx-a11y/media-has-caption
            return <video className="pra-preview-media" src={state.url} controls />;
        }
        if (state.kind === 'audio') {
            // eslint-disable-next-line jsx-a11y/media-has-caption
            return <audio src={state.url} controls />;
        }
        return (
            <div className="pra-preview-message">
                <FileIcon size={36} aria-hidden="true" />
                Preview isn't available for this file type. Use Download to open it.
            </div>
        );
    };

    return (
        <div className="apr-overlay" onClick={onClose}>
            <div
                className="apr-modal pra-modal"
                role="dialog"
                aria-modal="true"
                aria-label={`Attachment preview: ${filename}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="apr-header">
                    <h3 className="pra-title" title={filename}>
                        {filename}
                        {total > 1 && <span className="pra-counter">{index + 1} / {total}</span>}
                    </h3>
                    <div className="pra-header-actions">
                        {state.url && (
                            <a className="pra-download" href={state.url} download={filename} aria-label={`Download ${filename}`}>
                                <Download size={15} /> Download
                            </a>
                        )}
                        <button type="button" className="apr-close" onClick={onClose} aria-label="Close preview">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="pra-preview-body">
                    {total > 1 && (
                        <button type="button" className="pra-nav pra-nav-prev" onClick={goPrev} aria-label="Previous attachment">
                            <ChevronLeft size={20} />
                        </button>
                    )}
                    {renderBody()}
                    {total > 1 && (
                        <button type="button" className="pra-nav pra-nav-next" onClick={goNext} aria-label="Next attachment">
                            <ChevronRight size={20} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function PurchaseRequestAttachments({ requestId, attachments = [] }) {
    const [thumbnails, setThumbnails] = useState({});
    const [previewIndex, setPreviewIndex] = useState(null);
    const imageIds = attachments
        .filter((attachment) => attachment?._id && kindOf(mimeFromName(attachment)) === 'image')
        .map((attachment) => attachment._id)
        .join(',');

    useEffect(() => {
        setThumbnails({});
        if (!requestId || !imageIds || !canCreateObjectUrl()) return undefined;
        let cancelled = false;
        const urls = [];
        imageIds.split(',').forEach(async (attachmentId) => {
            const attachment = attachments.find((file) => file._id === attachmentId);
            try {
                const { url } = await fetchAttachment(requestId, attachment);
                if (cancelled) {
                    URL.revokeObjectURL(url);
                    return;
                }
                urls.push(url);
                setThumbnails((prev) => ({ ...prev, [attachmentId]: url }));
            } catch (error) {
                // No thumbnail; the tile still opens the preview.
            }
        });
        return () => {
            cancelled = true;
            urls.forEach((url) => URL.revokeObjectURL(url));
        };
        // attachments is re-created on every render; imageIds tracks its content.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestId, imageIds]);

    const previewable = attachments.filter((attachment) => attachment?._id);
    if (previewable.length === 0) return null;

    return (
        <div className="pra-root">
            <div className="pra-grid">
                {previewable.map((attachment, index) => {
                    const filename = attachment.filename || 'Attachment';
                    const isImage = kindOf(mimeFromName(attachment)) === 'image';
                    const isPdf = mimeFromName(attachment) === 'application/pdf';
                    const thumbnail = thumbnails[attachment._id];
                    return (
                        <button
                            type="button"
                            key={attachment._id}
                            className="pra-tile"
                            title={filename}
                            aria-label={`Preview ${filename}`}
                            onClick={() => setPreviewIndex(index)}
                        >
                            <span className="pra-tile-media">
                                {thumbnail ? (
                                    <img src={thumbnail} alt={filename} />
                                ) : isImage ? (
                                    <ImageIcon size={26} aria-hidden="true" />
                                ) : isPdf ? (
                                    <FileText size={26} aria-hidden="true" />
                                ) : (
                                    <FileIcon size={26} aria-hidden="true" />
                                )}
                                {!isImage && extensionOf(attachment) && (
                                    <span className="pra-tile-ext">{extensionOf(attachment).toUpperCase()}</span>
                                )}
                            </span>
                            <span className="pra-tile-name">{filename}</span>
                        </button>
                    );
                })}
            </div>

            {previewIndex !== null && (
                <AttachmentPreviewModal
                    requestId={requestId}
                    attachments={previewable}
                    startIndex={previewIndex}
                    thumbnails={thumbnails}
                    onClose={() => setPreviewIndex(null)}
                />
            )}
        </div>
    );
}
